import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { Pressable, SxProp, View } from 'dripsy';
import { t } from 'i18next';
import React, { useCallback, useContext, useState } from 'react';

import { UniButton } from '@/design-system/components/buttons';
import { FilterButton } from '@/design-system/components/buttons/filterButton/FilterButton';
import { Icon } from '@/design-system/components/icons/Icon';
import { TextCaption, TextTitle } from '@/design-system/components/typography';
import { keyGenerator } from '@/utils/keyGenerator';
import { LayoutContext } from 'contexts/LayoutContext';
import { AccountManagementStackRoutes, AccountManagementStackScreenProps } from '../../account.navigator';

export interface BottomSheetFilterValues extends Record<string, Array<string> | undefined> {}

export interface BottomSheetFilterOptionItem {
  label: string;
  value: string;
}

export interface BottomSheetFilterOptions
  extends Array<{
    label: string;
    name: string;
    options: Array<BottomSheetFilterOptionItem>;
    singleSelect?: boolean;
  }> {}

export interface BottomSheetFilterProps {
  bottomSheetRef: React.RefObject<BottomSheet>;
  values: BottomSheetFilterValues;
  filteringNumber: number;
  onClose: () => void;
  onChange: (tempFilters: BottomSheetFilterValues) => void;
  title?: string;
  options: BottomSheetFilterOptions;
}

export const BottomSheetFilter: React.FC<AccountManagementStackScreenProps<AccountManagementStackRoutes.ListBottomSheetFilter>> = ({
  route: {
    params: { bottomSheetRef, title, options, values, onChange }
  },
  navigation
}) => {
  const layout = useContext(LayoutContext);
  const [tempFilters, setTempFilters] = useState(values);
  const onTempFilter = (key: keyof typeof tempFilters, value: string) => {
    // Check if singleSelect
    const singleSelect = options.find((option) => option.name === key)?.singleSelect;
    if (singleSelect) {
      setTempFilters((prev) => ({ ...prev, [key]: [value] }));
    }
    // If not single select == multi select, if already selected, remove, else add
    else {
      setTempFilters((prev) => {
        const prevValue = prev[key] || [];
        const newValue = prevValue.includes(value) ? prevValue.filter((item) => item !== value) : [...prevValue, value];
        return { ...prev, [key]: newValue };
      });
    }
  };

  const onBack = () => {
    bottomSheetRef.current?.close();
  };

  const onReset = () => {
    setTempFilters({});
  };

  // Get dimensions
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);
  // Bottom Sheet on mobile
  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        onClose={() => navigation.goBack()}
        index={0}
        ref={bottomSheetRef}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View sx={styles.bottomsheetHeadHandler} />}>
        <View sx={{ height: 'auto', width, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
          <View sx={styles.header}>
            <Pressable onPress={onBack}>
              <Icon name="cross" width={24} />
            </Pressable>
            <TextTitle semiBold>{title || t('earn-invest::glossary.mobile-filter')}</TextTitle>
            <Pressable onPress={onReset}>
              <TextCaption sx={{ color: '$hyperlink' }}>{t('earn-invest::glossary.mobile-reset')}</TextCaption>
            </Pressable>
          </View>
          <View sx={{ paddingX: '$4', pt: '$8', pb: '$5', gap: '$5' }}>
            {options.map((option) => (
              <View key={keyGenerator('bottomsheet-filter')}>
                <TextCaption semiBold sx={{ mb: '$2', color: '$textPrimary' }}>
                  {option.label}
                </TextCaption>
                <View sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: '$2' }}>
                  {option.options.map((item) => {
                    const isSelected = !!tempFilters[option.name]?.includes(item.value);
                    return (
                      <FilterButton
                        key={keyGenerator(item.value)}
                        label={item.label}
                        onPress={() => onTempFilter(option.name, item.value)}
                        isSelected={isSelected}
                        value={item.value}
                      />
                    );
                  })}
                </View>
              </View>
            ))}
          </View>
          <UniButton
            onPress={() => {
              onChange(tempFilters);
              onBack();
            }}
            label={t('earn-invest::glossary.mobile-apply', { number: Object.values(tempFilters).flat().length })}
            wrapperSx={{ margin: '$5', mt: 0 }}
            size="default"
          />
        </View>
      </BottomSheet>
    </View>
  );
};

enum StyleName {
  bottomsheetHeadHandler = 'bottomsheetHeadHandler',
  header = 'header'
}

const styles: Record<StyleName, SxProp> = {
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  },
  header: { paddingX: '$4', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }
};
